import { Injectable, inject } from '@angular/core';
import {
  DocumentManagementApi,
  type CreateFolderRequest,
  type FolderDetailResponse,
  type FolderListResponse,
  type FolderResponse,
  type PatchFolder,
} from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Thin wrapper over the generated `DocumentManagementApi`.
 *
 * The generated client exposes promise-returning methods. This service stays promise-first to
 * match the existing object-views service style (see `ObjectDefinitionsService` for precedent).
 */
@Injectable({ providedIn: 'root' })
export class FoldersService {
  private readonly api = inject(DocumentManagementApi);

  listChildren(
    parentId: string | null,
    options: { page?: number; size?: number } = {},
  ): Promise<FolderListResponse> {
    return this.api.listFolderChildren({
      parentId: parentId ?? undefined,
      page: options.page,
      size: options.size,
    });
  }

  getById(id: string): Promise<FolderDetailResponse> {
    return this.api.getFolderById({ id });
  }

  /**
   * Looks up the folder at `path`. Triggers lazy-creation of managed namespace folders
   * (e.g. `/objects/<type>/<id>`); see `FolderService.findByPath` on the backend.
   */
  getByPath(path: string): Promise<FolderDetailResponse> {
    return this.api.getFolderByPath({ path });
  }

  create(request: CreateFolderRequest): Promise<FolderResponse> {
    return this.api.createFolder({ createFolderRequest: request });
  }

  update(id: string, patch: PatchFolder): Promise<FolderResponse> {
    return this.api.updateFolder({ id, patchFolder: patch });
  }

  /**
   * Move a folder under another parent folder.
   */
  move(id: string, targetParentId: string): Promise<FolderResponse> {
    const patchFolder: PatchFolder = { parentId: targetParentId };
    return this.api.updateFolder({ id, patchFolder });
  }

  /**
   * Move a folder to the tenant root. The backend's merge-patch handler reads a literal
   * `parent_id: null` as "promote to root"; the generated TS client types `parentId` as
   * `string | undefined` only, so we cast at the boundary to thread the null through.
   */
  moveToRoot(id: string): Promise<FolderResponse> {
    const patchFolder = { parentId: null } as unknown as PatchFolder;
    return this.api.updateFolder({ id, patchFolder });
  }

  /**
   * Hard-delete a folder. Cascades to the entire subtree; every contained document is soft-deleted
   * (its `original_path` is captured) so the user can restore individual documents later.
   * Folders themselves are NEVER soft-deleted in the new model.
   */
  delete(id: string): Promise<void> {
    return this.api.deleteFolder({ id });
  }

  star(id: string): Promise<FolderDetailResponse> {
    return this.api.starFolder({ id });
  }

  unstar(id: string): Promise<FolderDetailResponse> {
    return this.api.unstarFolder({ id });
  }
}
